import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { PanelLeft } from 'lucide-react'

import { SIDEBAR_ID } from './constants'
import OrgSwitcher from './OrgSwitcher'

import styles from './styles.module.scss'

import AvatarImage from '@/components/AvatarImage'
import { selectCurrentEnterprise, selectUser } from '@/store/features/user/userSlice'
import { logout } from '@/utils/auth'
import { clearAuthInterruption } from '@/utils/authInterruption'

interface MenuProps {
  setOpen: (open: boolean) => void
  isOpen: boolean
  preSetup?: boolean
}

const Menu = ({ setOpen, isOpen, preSetup }: MenuProps) => {
  const user = useSelector(selectUser)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // If user clicks outside, close it
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest(`.${styles.menu}`)) {
        setOpen(false)
      }
    }

    // Add listener after 100ms to prevent closing immediately
    if (isOpen) {
      setTimeout(() => document.addEventListener('click', handleClick), 100)
    } else document.removeEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [isOpen, setOpen])

  return (
    <div className={styles.menu}>
      <div className={styles.menuHeader}>
        <div className={styles.name}>{user.name}</div>
        <div className={styles.email}>{user.email}</div>
      </div>
      <div className={styles.divider} />
      {!preSetup && (
        <div
          className={styles.menuItem}
          onClick={() => {
            navigate('/app/settings')
            setOpen(false)
          }}
        >
          Settings
        </div>
      )}
      {!preSetup && (
        <div
          className={styles.menuItem}
          onClick={() => {
            navigate('/app/privacy')
            setOpen(false)
          }}
        >
          Privacy
        </div>
      )}
      <div
        className={styles.menuItem}
        onClick={() => {
          navigate('/app/settings')
          clearAuthInterruption()
          logout(dispatch)
        }}
      >
        Logout
      </div>
    </div>
  )
}

interface NavProps {
  preSetup?: boolean
  sidebarToggle?: { expanded: boolean; onToggle: () => void }
}

const Nav = ({ preSetup, sidebarToggle }: NavProps) => {
  const user = useSelector(selectUser)
  const [isMenuOpen, setMenuOpen] = useState(false)
  const enterprise = useSelector(selectCurrentEnterprise)

  // Finishes click event before opening modal
  const toggleModal = () => setTimeout(() => setMenuOpen(!isMenuOpen), 0)

  return (
    <div className={styles.nav}>
      <div className={styles.navStart}>
        {sidebarToggle && (
          <button
            type="button"
            className={styles.sidebarToggle}
            aria-label={sidebarToggle.expanded ? 'Hide navigation' : 'Show navigation'}
            aria-expanded={sidebarToggle.expanded}
            aria-controls={SIDEBAR_ID}
            onClick={sidebarToggle.onToggle}
          >
            <PanelLeft size={18} aria-hidden="true" />
          </button>
        )}
        {!enterprise ? <div className={styles.name}>Getting started</div> : <OrgSwitcher />}
      </div>
      <div className={styles.account}>
        <AvatarImage className={styles.avatar} avatarUrl={user.avatarUrl} onClick={toggleModal} />
      </div>
      {isMenuOpen && <Menu setOpen={setMenuOpen} isOpen={isMenuOpen} preSetup={preSetup} />}
    </div>
  )
}

export default Nav
